import { API_CONFIG } from '../config/apiConfig'

class ApiService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL
    this.timeout = API_CONFIG.REQUEST_TIMEOUT
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      const data = await response.json()

      if (!response.ok) {
        if (data.code === 'IS_EXPIRED_SUBS') {
          return {
            success: false,
            error: data.error || 'Erreur serveur',
            code: data.code || 'UNKNOWN_ERROR',
            shopId: data.shopId || null,
          }
        }
        return {
          success: false,
          error: data.error || 'Erreur serveur',
          code: data.code || 'UNKNOWN_ERROR',
        }
      }

      return data
    } catch (error) {
      clearTimeout(timeoutId)

      if (error.name === 'AbortError') {
        return {
          success: false,
          error: 'Délai de connexion dépassé',
          code: 'TIMEOUT',
        }
      }

      if (!navigator.onLine) {
        return {
          success: false,
          error: 'Aucune connexion internet',
          code: 'OFFLINE',
        }
      }

      return {
        success: false,
        error: 'Erreur de connexion au serveur',
        code: 'NETWORK_ERROR',
      }
    }
  }

  async checkOnline() {
    // if (!navigator.onLine) {
    //   return {
    //     success: false,
    //     error:
    //       'Aucune connexion internet. Cette action nécessite une connexion.',
    //     code: 'OFFLINE',
    //   }
    // }
    // return { success: true }

    if (!navigator.onLine) return false

    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 3000)

      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      })

      clearTimeout(timeout)
      return { success: response.ok }
    } catch {
      return {
        success: false,
        error:
          'Aucune connexion internet. Cette action nécessite une connexion.',
        code: 'OFFLINE',
      }
    }
  }

  async requestLoginCode(email) {
    const onlineCheck = await await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.REQUEST_LOGIN_CODE, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async verifyLoginCode(email, code) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.VERIFY_LOGIN_CODE, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
  }

  async requestRegisterCodes(email, phone, turnstileToken) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.REQUEST_REGISTER_CODES, {
      method: 'POST',
      body: JSON.stringify({ email, phone, turnstileToken }),
    })
  }

  async verifyRegisterCodes(email, phone, emailCode, phoneCode) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.VERIFY_REGISTER_CODES, {
      method: 'POST',
      body: JSON.stringify({ email, phone, emailCode, phoneCode }),
    })
  }

  async verifyToken(token) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.VERIFY_TOKEN, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async logout(token) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.LOGOUT, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async initiatePayment(
    planId,
    paymentMethod,
    registerData,
    renewSubscriptionData,
  ) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.INITIATE_PAYMENT, {
      method: 'POST',
      body: JSON.stringify({
        type: renewSubscriptionData?.renewSubscription ? 'renew' : 'register',
        planId,
        paymentMethod,
        registerData,
        renewSubscriptionData,
      }),
    })
  }

  async registerFreeTrial(planId, registerData) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.REGISTER_FREE_TRIAL, {
      method: 'POST',
      body: JSON.stringify({
        ...registerData,
        planId,
      }),
    })
  }

  async verifyPayment(paymentId) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(
      `${API_CONFIG.ENDPOINTS.VERIFY_PAYMENT}?paymentId=${paymentId}`,
      {
        method: 'GET',
      },
    )
  }

  async renewSubscription(token, planId, paymentMethod) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.RENEW_SUBSCRIPTION, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ planId, paymentMethod }),
    })
  }

  async checkSubscription(token) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.CHECK_SUBSCRIPTION, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  // Gestion manager
  async addManager(token, managerData) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.ADD_MANAGER, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(managerData),
    })
  }

  async getManagers(token) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.GET_MANAGERS, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async updateManager(token, managerId, managerData) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.UPDATE_MANAGER, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ managerId, ...managerData }),
    })
  }

  async deleteManager(token, managerId) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.DELETE_MANAGER, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ managerId }),
    })
  }

  async requestManagerOTP(phone, shopId, turnstileToken) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.REQUEST_MANAGER_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone, shopId, turnstileToken }),
    })
  }

  async verifyManagerOTP(phone, code, shopId) {
    const onlineCheck = await this.checkOnline()
    if (!onlineCheck.success) return onlineCheck

    return this.request(API_CONFIG.ENDPOINTS.VERIFY_MANAGER_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone, code, shopId }),
    })
  }
}

export default new ApiService()
